import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutGrid, Users, Calendar, Settings, Upload, LogOut, Menu, X, Dumbbell, BookOpen, Snowflake, ClipboardCheck, Flame, GraduationCap, Cpu, Video, Building2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { ShadowBanner } from "@/components/ShadowLog";
import { useFusionAccess } from "@/lib/useFusionAccess";
import NotificationBell from "@/components/NotificationBell";
import { useCan } from "@/lib/permissions";

const NAV_ALL = [
  { to: "/dashboard", label: "Overview", icon: LayoutGrid, end: true, perm: null },
  { to: "/dashboard/roster", label: "Roster", icon: Users, perm: "view_biometrics" },
  { to: "/dashboard/calendar", label: "Calendar", icon: Calendar, perm: "edit_calendar" },
  { to: "/dashboard/routine", label: "Warm-Up", icon: Dumbbell, perm: null },
  { to: "/dashboard/recovery", label: "Recovery", icon: Snowflake, perm: null },
  { to: "/dashboard/workload", label: "Workload", icon: ClipboardCheck, perm: "view_biometrics" },
  { to: "/dashboard/replays", label: "Replays", icon: Flame, perm: "view_biometrics" },
  { to: "/dashboard/motioniq", label: "MotionIQ", icon: Cpu, perm: "view_biometrics" },
  { to: "/dashboard/fusion", label: "Fusion", icon: Flame, perm: null },
  { to: "/dashboard/livecoach", label: "Live Coach", icon: Video, perm: null },
  { to: "/dashboard/academy", label: "Academy", icon: Building2, perm: null },
  { to: "/dashboard/lessons", label: "Lessons", icon: GraduationCap, perm: null },
  { to: "/dashboard/import", label: "CSV Import", icon: Upload, perm: "edit_roster" },
  { to: "/dashboard/settings", label: "Settings", icon: Settings, perm: null },
  { to: "/dashboard/help", label: "Help", icon: BookOpen, perm: null },
];

export default function CoachLayout() {
  const { user, team, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  const canCalendar = useCan("edit_calendar");
  const canRoster = useCan("edit_roster");
  const access = useFusionAccess();

  // Fusion-only teams (signed up via the Fusion landing page, no BioHacker plan)
  // get the Fusion dashboard only — the full panel unlocks when they purchase.
  const fusionOnly = access.loaded && !access.biohacker_enabled;
  useEffect(() => {
    if (fusionOnly && location.pathname.startsWith("/dashboard") && location.pathname !== "/dashboard/fusion") {
      nav("/dashboard/fusion", { replace: true });
    }
  }, [fusionOnly, location.pathname]); // eslint-disable-line

  const NAV = NAV_ALL.filter((item) => {
    if (item.to === "/dashboard/fusion") return fusionOnly || !access.loaded || access.fusion_enabled;
    if (item.to === "/dashboard/academy") return !fusionOnly && team?.org_type === "academy";
    if (fusionOnly) return false;
    if (!item.perm) return true;
    if (item.perm === "edit_calendar") return canCalendar;
    if (item.perm === "edit_roster") return canRoster;
    return true;
  });

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--ink-1)" }}>
      <aside className={`${open ? "fixed inset-0 z-40 md:relative md:inset-auto" : "hidden md:flex"} md:flex w-64 flex-col border-r border-[var(--ink-border)]`} style={{ backgroundColor: "var(--ink-0)" }}>
        <div className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center justify-between">
          <Logo size="md" variant="lockup" linkTo="/dashboard" dark />
          <button className="md:hidden text-white/60" onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 border-b border-[var(--ink-border)]">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Tenant</div>
          <div className="font-display font-bold text-white mt-1 truncate" data-testid="coach-team-name">
            {team?.branding?.display_name || team?.name || "Loading..."}
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.to} to={item.to} end={item.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}
                style={({ isActive }) => isActive ? { backgroundColor: "var(--brand-secondary)" } : {}}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[var(--ink-border)] p-4">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full ring-1 ring-white/20" data-testid="coach-avatar" />
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="coach-avatar-fallback">
                {(user?.name || "C")[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-sm text-white truncate font-medium">{user?.name || "Coach"}</div>
              <div className="text-xs text-white/40 truncate" data-testid="coach-user-email">{user?.email}</div>
            </div>
          </div>
          <Button variant="outline" className="w-full border-white/15 text-white/80 bg-white/5 hover:bg-white/10" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <ShadowBanner />
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-[var(--ink-border)]" style={{ backgroundColor: "var(--ink-0)" }}>
          <button onClick={() => setOpen(true)} aria-label="Open menu" className="text-white" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
          <Logo size="sm" variant="lockup" linkTo="/dashboard" dark />
          <NotificationBell />
        </header>
        <div className="hidden md:flex items-center justify-end px-8 py-3 border-b border-[var(--ink-border)] gap-2" style={{ backgroundColor: "var(--ink-0)" }}>
          <NotificationBell />
        </div>
        <main className="flex-1 p-4 md:p-8 text-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
